/**
 * Storage Manager - IndexedDB & LocalStorage
 * Handles data persistence for roast sessions and profiles
 */

class StorageManager {
    constructor() {
        this.dbName = 'ArtisanRoasterDB';
        this.dbVersion = 1;
        this.db = null;
    }

    /**
     * Initialize IndexedDB
     */
    async init() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = () => {
                console.error('IndexedDB error:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                console.log('IndexedDB initialized');
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create object stores
                if (!db.objectStoreNames.contains('sessions')) {
                    const sessionsStore = db.createObjectStore('sessions', { keyPath: 'id', autoIncrement: true });
                    sessionsStore.createIndex('timestamp', 'timestamp', { unique: false });
                    sessionsStore.createIndex('roastLevel', 'roastLevel', { unique: false });
                }

                if (!db.objectStoreNames.contains('profiles')) {
                    const profilesStore = db.createObjectStore('profiles', { keyPath: 'id', autoIncrement: true });
                    profilesStore.createIndex('name', 'name', { unique: false });
                }

                console.log('IndexedDB schema created');
            };
        });
    }

    /**
     * Save roast session
     */
    async saveSession(sessionData) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['sessions'], 'readwrite');
            const store = transaction.objectStore('sessions');

            const session = {
                ...sessionData,
                timestamp: Date.now(),
                savedAt: new Date().toISOString()
            };

            const request = store.add(session);

            request.onsuccess = () => {
                console.log('Session saved:', request.result);
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Save session error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get all sessions
     */
    async getAllSessions(filters = {}) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['sessions'], 'readonly');
            const store = transaction.objectStore('sessions');
            const request = store.getAll();

            request.onsuccess = () => {
                let sessions = request.result;

                // Apply filters
                if (filters.date) {
                    const filterDate = new Date(filters.date).toDateString();
                    sessions = sessions.filter(s =>
                        new Date(s.timestamp).toDateString() === filterDate
                    );
                }

                if (filters.roastLevel) {
                    sessions = sessions.filter(s => s.roastLevel === filters.roastLevel);
                }

                // Sort by timestamp descending
                sessions.sort((a, b) => b.timestamp - a.timestamp);

                resolve(sessions);
            };

            request.onerror = () => {
                console.error('Get sessions error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get session by ID
     */
    async getSession(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['sessions'], 'readonly');
            const store = transaction.objectStore('sessions');
            const request = store.get(id);

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Get session error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Delete session
     */
    async deleteSession(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['sessions'], 'readwrite');
            const store = transaction.objectStore('sessions');
            const request = store.delete(id);

            request.onsuccess = () => {
                console.log('Session deleted:', id);
                resolve();
            };

            request.onerror = () => {
                console.error('Delete session error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Save roast profile
     */
    async saveProfile(profileData) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['profiles'], 'readwrite');
            const store = transaction.objectStore('profiles');

            const profile = {
                ...profileData,
                updatedAt: new Date().toISOString()
            };

            const request = profile.id ? store.put(profile) : store.add(profile);

            request.onsuccess = () => {
                console.log('Profile saved:', request.result);
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Save profile error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Get all profiles
     */
    async getAllProfiles() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['profiles'], 'readonly');
            const store = transaction.objectStore('profiles');
            const request = store.getAll();

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                console.error('Get profiles error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Delete profile
     */
    async deleteProfile(id) {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['profiles'], 'readwrite');
            const store = transaction.objectStore('profiles');
            const request = store.delete(id);

            request.onsuccess = () => {
                console.log('Profile deleted:', id);
                resolve();
            };

            request.onerror = () => {
                console.error('Delete profile error:', request.error);
                reject(request.error);
            };
        });
    }

    /**
     * Export all data
     */
    async exportAllData() {
        const sessions = await this.getAllSessions();
        const profiles = await this.getAllProfiles();
        const settings = this.getSettings();

        const exportData = {
            version: this.dbVersion,
            exportedAt: new Date().toISOString(),
            sessions,
            profiles,
            settings
        };

        return exportData;
    }

    /**
     * Import data
     */
    async importData(importData) {
        if (!this.db) await this.init();

        // Import sessions
        if (importData.sessions) {
            for (const session of importData.sessions) {
                delete session.id; // Let DB generate new IDs
                await this.saveSession(session);
            }
        }

        // Import profiles
        if (importData.profiles) {
            for (const profile of importData.profiles) {
                delete profile.id;
                await this.saveProfile(profile);
            }
        }

        // Import settings
        if (importData.settings) {
            this.saveSettings(importData.settings);
        }

        console.log('Data imported successfully');
    }

    /**
     * Get settings from LocalStorage
     */
    getSettings() {
        const defaultSettings = {
            enableAlerts: true,
            tempHigh: 230,
            enableSound: true,
            autoSave: true,
            sampleRate: 10
        };

        const stored = localStorage.getItem('roasterSettings');
        return stored ? { ...defaultSettings, ...JSON.parse(stored) } : defaultSettings;
    }

    /**
     * Save settings to LocalStorage
     */
    saveSettings(settings) {
        localStorage.setItem('roasterSettings', JSON.stringify(settings));
        console.log('Settings saved');
    }

    /**
     * Get device configuration
     */
    getDeviceConfig() {
        const stored = localStorage.getItem('deviceConfig');
        return stored ? JSON.parse(stored) : null;
    }

    /**
     * Save device configuration
     */
    saveDeviceConfig(config) {
        localStorage.setItem('deviceConfig', JSON.stringify(config));
        console.log('Device config saved');
    }

    /**
     * Clear all data
     */
    async clearAll() {
        if (!this.db) await this.init();

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(['sessions', 'profiles'], 'readwrite');

            transaction.objectStore('sessions').clear();
            transaction.objectStore('profiles').clear();

            transaction.oncomplete = () => {
                localStorage.clear();
                console.log('All data cleared');
                resolve();
            };

            transaction.onerror = () => {
                console.error('Clear data error:', transaction.error);
                reject(transaction.error);
            };
        });
    }
}

export default StorageManager;
